// ═══════════════════════════════════════════════════════════════
// ModuleNav — Responsive module sub-navigation
// Desktop: flat buttons for ungrouped items, dropdown buttons for groups.
// Mobile: single hamburger menu.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Group, Button, Menu, Text, Badge } from '../gab/GabComponents';
import { IconMenu2, IconChevronDown } from '@tabler/icons-react';
import { brand } from '../../lib/colors';
import type { NavItem } from '../../types/nav';

interface NavGroup {
  label: string;
  items: NavItem[];
}

interface Props {
  items: NavItem[];
  groups?: NavGroup[];
  accentColor?: string;
  extra?: React.ReactNode;
}

// Dropdown for a named group of nav items
function GroupDropdown({ group, pathname, navigate, color }: {
  group: NavGroup;
  pathname: string;
  navigate: (path: string) => void;
  color: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const isGroupActive = group.items.some(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );
  const activeItem = group.items.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '6px 14px', fontSize: 13, fontWeight: 500,
          borderRadius: 6, cursor: 'pointer', border: '1px solid',
          transition: 'all 0.15s', height: 30,
          ...(isGroupActive ? {
            background: color, color: '#fff', borderColor: color,
          } : {
            background: 'rgba(0,0,0,0.04)', color: '#444', borderColor: 'transparent',
          }),
        }}
      >
        {activeItem ? (
          <>
            <activeItem.icon size={14} />
            <span>{activeItem.label}</span>
            <span style={{ opacity: 0.6, fontSize: 11, marginLeft: 1 }}>({group.label})</span>
          </>
        ) : (
          <span>{group.label}</span>
        )}
        <IconChevronDown size={13} style={{ marginLeft: 2, opacity: 0.6, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 300,
          background: '#fff', border: '1px solid #e2e4e8',
          borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          minWidth: 200, overflow: 'hidden',
        }}>
          {group.items.map(item => {
            const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
            return (
              <button
                key={item.path}
                onClick={() => { navigate(item.path); setOpen(false); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  width: '100%', padding: '9px 14px', fontSize: 13,
                  background: active ? `${color}18` : 'transparent',
                  color: active ? color : '#333',
                  fontWeight: active ? 600 : 400,
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                  borderLeft: active ? `3px solid ${color}` : '3px solid transparent',
                }}
              >
                <item.icon size={14} />
                {item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ModuleNav({ items, groups, accentColor, extra }: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const color = accentColor || brand.blurple;

  const activeItem = items.find(item =>
    item.exact ? pathname === item.path : pathname.startsWith(item.path)
  );

  const renderDesktop = () => {
    if (groups && groups.length > 0) {
      return groups.map((group, gi) => {
        // Unnamed group → render items individually
        if (!group.label) {
          return (
            <React.Fragment key={gi}>
              {group.items.map(item => {
                const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
                return (
                  <Button
                    key={item.path}
                    variant={active ? 'filled' : 'light'}
                    color={active ? color : 'gray'}
                    size="sm"
                    leftSection={<item.icon size={15} />}
                    onClick={() => navigate(item.path)}
                  >
                    {item.label}
                  </Button>
                );
              })}
              {/* Divider after unnamed group if named groups follow */}
              {gi < groups.length - 1 && groups[gi + 1]?.label && (
                <div style={{ width: 1, height: 20, background: '#dde1e7', margin: '0 4px', alignSelf: 'center' }} />
              )}
            </React.Fragment>
          );
        }
        // Named group → dropdown
        return (
          <GroupDropdown
            key={gi}
            group={group}
            pathname={pathname}
            navigate={navigate}
            color={color}
          />
        );
      });
    }

    // Flat (no groups)
    return items.map(item => {
      const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
      return (
        <Button
          key={item.path}
          variant={active ? 'filled' : 'light'}
          color={active ? color : 'gray'}
          size="sm"
          leftSection={<item.icon size={15} />}
          rightSection={item.badge ? <Badge size="xs" circle color="red" variant="filled">{item.badge}</Badge> : undefined}
          onClick={() => navigate(item.path)}
        >
          {item.label}
        </Button>
      );
    });
  };

  const renderMenuItems = () => {
    if (groups && groups.length > 0) {
      return groups.map((group, gi) => (
        <div key={gi}>
          {gi > 0 && <Menu.Divider />}
          {group.label && <Menu.Label>{group.label}</Menu.Label>}
          {group.items.map(item => {
            const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
            return (
              <Menu.Item
                key={item.path}
                leftSection={<item.icon size={14} />}
                onClick={() => navigate(item.path)}
                style={active ? { backgroundColor: 'var(--mantine-color-blue-light)', fontWeight: 600 } : undefined}
              >
                {item.label}
              </Menu.Item>
            );
          })}
        </div>
      ));
    }
    return items.map(item => {
      const active = item.exact ? pathname === item.path : pathname.startsWith(item.path);
      return (
        <Menu.Item
          key={item.path}
          leftSection={<item.icon size={14} />}
          rightSection={item.badge ? <Badge size="xs" circle color="red" variant="filled">{item.badge}</Badge> : undefined}
          onClick={() => navigate(item.path)}
          style={active ? { backgroundColor: 'var(--mantine-color-blue-light)', fontWeight: 600 } : undefined}
        >
          {item.label}
        </Menu.Item>
      );
    });
  };

  return (
    <>
      {/* Desktop: button bar — hidden on mobile via CSS */}
      <div className="module-nav-desktop" style={{
        flexWrap: 'wrap', alignItems: 'center', gap: 6,
        borderBottom: '1px solid var(--frost)', paddingBottom: 12, marginBottom: 'var(--spacing-4)',
      }}>
        {renderDesktop()}
        {extra && <div style={{ marginLeft: 'auto' }}>{extra}</div>}
      </div>

      {/* Mobile: hamburger menu — hidden on desktop via CSS */}
      <div className="module-nav-mobile" style={{
        alignItems: 'center', gap: 8,
        borderBottom: '1px solid var(--frost)', paddingBottom: 8, marginBottom: 'var(--spacing-4)',
      }}>
        <Menu shadow="lg" width={250} position="bottom-start" withArrow>
          <Menu.Target>
            <Button variant="light" color="gray" size="sm" leftSection={<IconMenu2 size={16} />}>
              {activeItem?.label || 'Navigate'}
            </Button>
          </Menu.Target>
          <Menu.Dropdown style={{ maxHeight: 400, overflowY: 'auto' }}>
            {renderMenuItems()}
          </Menu.Dropdown>
        </Menu>
        {extra && <div style={{ marginLeft: 'auto' }}>{extra}</div>}
      </div>
    </>
  );
}
